import { useEffect, useMemo, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { Empty } from "../components/ui";
import { relDate } from "../lib/format";
import type { ChatChannel, ChatDelivery, ChatPlatform, ChatEvent } from "../lib/types";
import * as work from "../lib/workClient";

/* Slack / Teams delivery. Configure incoming-webhook channels, choose which
 * events route to each, send a test, and watch the delivery log. The webhook
 * URL is stored server-side — only its host is ever shown. */

const EVENTS: { key: ChatEvent; label: string; hint: string }[] = [
  { key: "signal.risk", label: "Risk signals", hint: "New risk signals on any account" },
  { key: "churn.high", label: "Churn spikes", hint: "An account crosses into high churn risk" },
  { key: "renewal.updated", label: "Renewal moves", hint: "A renewal changes stage" },
  { key: "digest", label: "Daily digest", hint: "Your cockpit worklist, on a schedule" },
];
const platformIcon = (p: ChatPlatform) => (p === "slack" ? "bell" : "mail");

export default function Delivery() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [deliveries, setDeliveries] = useState<ChatDelivery[]>([]);
  const [adding, setAdding] = useState(false);
  const [live, setLive] = useState<boolean | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const refresh = async () => {
    const r = await work.loadChatChannels(ws); setMode(r.mode); setChannels(r.channels);
    setDeliveries(await work.loadChatDeliveries(ws));
  };
  useEffect(() => { refresh(); work.chatLive().then(setLive); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ws]);

  const say = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 2600); };

  const toggle = async (c: ChatChannel) => { await work.updateChatChannel(mode, ws, c.id, { active: !c.active }); refresh(); };
  const toggleEvent = async (c: ChatChannel, e: ChatEvent) => {
    const events = c.events.includes(e) ? c.events.filter((x) => x !== e) : [...c.events, e];
    await work.updateChatChannel(mode, ws, c.id, { events }); refresh();
  };
  const remove = async (c: ChatChannel) => { await work.deleteChatChannel(mode, ws, c.id); refresh(); };
  const test = async (c: ChatChannel) => { const d = await work.testChatChannel(mode, ws, c); say(d?.status === "success" ? `Test sent to ${c.name}` : `Test failed for ${c.name}`); refresh(); };
  const digest = async (c: ChatChannel) => { const d = await work.sendDigest(mode, ws, c.id, c); say(d?.status === "success" ? `Digest sent to ${c.name}` : `Digest failed`); refresh(); };

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Deliver</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Slack &amp; Teams delivery</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>Route the signals that matter — risk, churn, renewals, and your daily worklist — straight into the channels your team already lives in.</p>
        </div>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <span className={`mode-pill ${live ? "live" : ""}`}><i />{live ? "Live delivery" : "Simulated"}</span>
          <button className="btn btn--primary btn--sm" onClick={() => setAdding(true)}><Icon name="plus" size={14} /> Add channel</button>
        </div>
      </div>

      {flash && <div className="badge badge--good" style={{ marginBottom: 16 }}>{flash}</div>}

      <div style={{ display: "grid", gap: 12, marginBottom: 28 }}>
        {channels.length === 0 ? <div className="card"><div className="card__bd"><Empty>No channels yet — add a Slack or Teams webhook to start.</Empty></div></div> :
          channels.map((c) => (
            <div key={c.id} className="card">
              <div className="card__bd">
                <div className="row between" style={{ alignItems: "flex-start" }}>
                  <div className="row" style={{ gap: 12, alignItems: "center" }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: "var(--surface-2)", display: "grid", placeItems: "center", color: c.active ? "var(--good)" : "var(--faint)" }}>
                      <Icon name={platformIcon(c.platform)} size={18} />
                    </div>
                    <div>
                      <div className="row" style={{ gap: 8, alignItems: "center" }}>
                        <b style={{ fontSize: 15 }}>{c.name}</b>
                        <span className="tag" style={{ textTransform: "capitalize" }}>{c.platform}</span>
                        {!c.active && <span className="badge badge--neutral">Paused</span>}
                      </div>
                      <div className="faint" style={{ fontSize: 12, marginTop: 2 }}>{c.urlHost}{c.lastDeliveryAt ? ` · last delivery ${relDate(c.lastDeliveryAt)}` : " · no deliveries yet"}</div>
                    </div>
                  </div>
                  <div className="row" style={{ gap: 6 }}>
                    <button className="btn btn--ghost btn--sm" onClick={() => test(c)}>Send test</button>
                    <button className="btn btn--ghost btn--sm" onClick={() => digest(c)}>Push digest</button>
                    <button className="btn btn--ghost btn--sm" onClick={() => toggle(c)}>{c.active ? "Pause" : "Resume"}</button>
                    <button className="btn btn--ghost btn--sm" onClick={() => remove(c)} title="Remove"><Icon name="dot" size={14} /> Delete</button>
                  </div>
                </div>
                <div className="row" style={{ gap: 8, flexWrap: "wrap", marginTop: 14 }}>
                  {EVENTS.map((e) => {
                    const on = c.events.includes(e.key);
                    return (
                      <button key={e.key} className={`chip ${on ? "chip--active" : ""}`} onClick={() => toggleEvent(c, e.key)} title={e.hint}>
                        {on && <Icon name="check" size={12} />} {e.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
      </div>

      <div className="card">
        <div className="card__hd"><div><h3>Delivery log</h3><span className="sub">Most recent {mode === "api" ? "· live-gated by CHAT_LIVE" : "· simulated in demo"}</span></div></div>
        <div style={{ overflowX: "auto" }}>
          {deliveries.length === 0 ? <div className="card__bd"><Empty>No deliveries yet.</Empty></div> : (
            <table className="table">
              <thead><tr><th>When</th><th>Channel</th><th>Event</th><th>Message</th><th>Status</th></tr></thead>
              <tbody>
                {deliveries.map((d) => (
                  <tr key={d.id} style={{ cursor: "default" }}>
                    <td className="faint" style={{ whiteSpace: "nowrap" }}>{relDate(d.at)}</td>
                    <td>{d.channelName}</td>
                    <td><span className="tag">{d.event}</span></td>
                    <td>{d.title}</td>
                    <td><span className={`badge badge--${d.status === "success" ? "good" : "risk"}`}>{d.status === "success" ? `${d.responseCode || "OK"}` : "failed"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {adding && <AddChannel onClose={() => setAdding(false)} onAdd={async (input) => { await work.createChatChannel(mode, ws, input); setAdding(false); refresh(); }} />}
    </div>
  );
}

function AddChannel({ onClose, onAdd }: { onClose: () => void; onAdd: (i: { platform: ChatPlatform; name: string; url: string; events: ChatEvent[] }) => void }) {
  const [platform, setPlatform] = useState<ChatPlatform>("slack");
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<ChatEvent[]>(["signal.risk", "churn.high"]);
  const valid = name.trim() && /^https?:\/\//.test(url);
  const flip = (e: ChatEvent) => setEvents((s) => s.includes(e) ? s.filter((x) => x !== e) : [...s, e]);
  const placeholder = useMemo(() => platform === "slack" ? "https://hooks.slack.com/services/…" : "https://outlook.office.com/webhook/…", [platform]);

  return (
    <div className="cmdk-overlay" onClick={onClose}>
      <div className="compose-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="compose-modal__hd"><div className="row" style={{ gap: 8 }}><Icon name="plug" size={16} /><b>Add a delivery channel</b></div><button className="icon-btn" onClick={onClose}><Icon name="dot" size={16} /></button></div>
        <div className="compose-modal__bd" style={{ display: "grid", gap: 14 }}>
          <div className="seg-control">
            <button className={platform === "slack" ? "on" : ""} onClick={() => setPlatform("slack")}>Slack</button>
            <button className={platform === "teams" ? "on" : ""} onClick={() => setPlatform("teams")}>Microsoft Teams</button>
          </div>
          <label style={{ display: "grid", gap: 5 }}>
            <span className="labeled__l">Channel name</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={platform === "slack" ? "#cs-alerts" : "CS Renewals"} />
          </label>
          <label style={{ display: "grid", gap: 5 }}>
            <span className="labeled__l">Incoming webhook URL</span>
            <input className="input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={placeholder} style={{ fontFamily: "var(--mono)", fontSize: 12.5 }} />
            <span className="faint" style={{ fontSize: 11.5 }}>Stored securely server-side — only the host is shown after saving.</span>
          </label>
          <div>
            <span className="labeled__l">Route these events</span>
            <div className="row" style={{ gap: 8, flexWrap: "wrap", marginTop: 8 }}>
              {EVENTS.map((e) => (
                <button key={e.key} className={`chip ${events.includes(e.key) ? "chip--active" : ""}`} onClick={() => flip(e.key)}>
                  {events.includes(e.key) && <Icon name="check" size={12} />} {e.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="card__hd" style={{ justifyContent: "flex-end", gap: 8, borderTop: "1px solid var(--line-soft)", borderBottom: "none" }}>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary btn--sm" disabled={!valid} onClick={() => onAdd({ platform, name: name.trim(), url, events })}>Add channel</button>
        </div>
      </div>
    </div>
  );
}
